import { BrandTraxListing, Country, ListingState, ListingStatus } from '../types/brandtraxListing';
import { getMarketplacePlatform } from './marketplaceMatcher';
import { toRegistrableDomain } from './registrableDomain';

export interface ReviewListing {
  url: string;
  title: string;
  price?: string;
  seller?: string;
  // Seller store URL - may come from backend as seller_store_url (snake_case) or sellerStoreUrl (camelCase)
  sellerStoreUrl?: string;
  seller_store_url?: string;
  marketplace?: string;
  image?: string;
  images?: string[];
  description?: string;
  stage?: 'search' | 'product' | 'seller';
  screenshot_url?: string;
  brand?: string;
  product?: string;
  // Item number - may come from backend as item_number (snake_case) or itemNumber (camelCase)
  itemNumber?: string;
  item_number?: string;
  relatedItems?: string[];
  related_items?: string[];
  // Stock/quantity - may come from backend as stock or quantity
  stock?: string;
  // Number sold - may come from backend as number_sold (snake_case) or numberSold (camelCase)
  numberSold?: string;
  number_sold?: string;
  [key: string]: any;
}

export interface StoreProduct {
  name: string;
  brand: string;
  description?: string;
  msrp?: {
    value: number;
    currency: string;
  };
  category?: string;
  url?: string;
  images?: string[];
}

export interface ListingCountryContext {
  sellerCountry?: string | null;
  marketplaceCountry?: string | null;
  platformIdentifier?: string;
}

/**
 * Parse a country code string into the BrandTrax Country enum.
 */
export function parseCountryCode(value?: string | null): Country | undefined {
  if (!value) return undefined;
  const normalized = value.trim().toUpperCase();
  if (!normalized || normalized === 'UNKNOWN') return undefined;
  if (Object.values(Country).includes(normalized as Country)) {
    return normalized as Country;
  }
  return undefined;
}

/**
 * Extract country from a platform identifier such as "DHGATE CN" or "AMAZON_US".
 */
export function countryFromPlatformIdentifier(platform?: string): Country | undefined {
  if (!platform) return undefined;
  const parts = platform.trim().split(/[\s_]+/);
  if (parts.length < 2) return undefined;
  return parseCountryCode(parts[parts.length - 1]);
}

/**
 * Seller country preferred; otherwise marketplace/platform country; then domain heuristics.
 */
export function resolveListingCountry(options: {
  domain?: string;
  sellerCountry?: string | null;
  marketplaceCountry?: string | null;
  platformIdentifier?: string;
}): Country {
  const fromSeller = parseCountryCode(options.sellerCountry);
  if (fromSeller) return fromSeller;

  const fromMarketplace = parseCountryCode(options.marketplaceCountry);
  if (fromMarketplace) return fromMarketplace;

  const fromPlatform = countryFromPlatformIdentifier(options.platformIdentifier);
  if (fromPlatform) return fromPlatform;

  return determineCountry(options.domain);
}

/**
 * Transforms a review listing to a BrandTrax Listing object
 */
export type ReviewListingTransformOptions = {
  analysisProfile?: 'BRAND_IP_MERCHANDISE';
  productCategory?: string;
};

export async function transformReviewListingToBrandTrax(
  reviewListing: ReviewListing,
  storeProduct?: StoreProduct,
  brandName?: string,
  productName?: string,
  countryContext?: ListingCountryContext,
  options?: ReviewListingTransformOptions
): Promise<BrandTraxListing> {
  // Extract marketplace domain from URL (strip leading www. and seller subdomains)
  const domain = reviewListing.url
    ? (() => {
        const h = new URL(reviewListing.url).hostname;
        const host = h.startsWith('www.') ? h.slice(4) : h;
        return toRegistrableDomain(host);
      })()
    : undefined;

  // Get marketplace platform identifier
  const platform = await getMarketplacePlatform(reviewListing.url);

  const country = resolveListingCountry({
    domain: reviewListing.marketplace || domain,
    sellerCountry: countryContext?.sellerCountry,
    marketplaceCountry: countryContext?.marketplaceCountry,
    platformIdentifier: platform ?? countryContext?.platformIdentifier,
  });

  // Parse price and currency with marketplace-aware detection
  const { price, currency } = parsePriceWithMarketplace(reviewListing.price, reviewListing.url);

  // Use store product data if available, otherwise fall back to review listing.
  // Brand-IP merchandise has no catalog SKU; the listing title must not become one.
  const brandIpMerchandise = options?.analysisProfile === 'BRAND_IP_MERCHANDISE';
  const brand = brandIpMerchandise
    ? brandName || storeProduct?.brand || 'Unknown Brand'
    : storeProduct?.brand || brandName || 'Unknown Brand';
  const product = brandIpMerchandise
    ? undefined
    : storeProduct?.name || productName || reviewListing.title;
  const description = brandIpMerchandise
    ? reviewListing.description
    : storeProduct?.description || reviewListing.description;
  const articleCategory = String(options?.productCategory || '').trim();

  // Prepare images array - use only listing images (filtered for deletions)
  const images: string[] = [];

  // Always use listing images, which are already filtered for deletions
  if (reviewListing.images && Array.isArray(reviewListing.images)) {
    images.push(...reviewListing.images);
  } else if (reviewListing.image) {
    images.push(reviewListing.image);
  }

  // Remove duplicates and filter out invalid URLs
  const uniqueImages = [...new Set(images)].filter(
    img =>
      img && typeof img === 'string' && (img.startsWith('http://') || img.startsWith('https://'))
  );

  // Extract filename from screenshot URL (store service expects filename only, not full URL)
  const screenshotFilename = reviewListing.screenshot_url
    ? reviewListing.screenshot_url.split('/').pop() || reviewListing.screenshot_url
    : undefined;

  // Handle snake_case to camelCase mapping for fields from scraper backend
  const sellerStoreUrl =
    reviewListing.sellerStoreUrl || reviewListing.seller_store_url || undefined;
  let itemNumber = reviewListing.itemNumber || reviewListing.item_number || undefined;
  // Fallback: extract ASIN/item number from URL (e.g. Amazon /dp/ASIN or /gp/product/ASIN)
  if (!itemNumber && reviewListing.url) {
    const fromUrl = extractItemNumberFromUrl(reviewListing.url);
    if (fromUrl) itemNumber = fromUrl;
  }
  const stock = reviewListing.stock ? parseFloat(reviewListing.stock) : undefined;
  const numberSold =
    reviewListing.numberSold || reviewListing.number_sold
      ? parseInt(reviewListing.numberSold || reviewListing.number_sold || '0', 10)
      : undefined;

  const rawRelated = reviewListing.relatedItems || reviewListing.related_items;
  let relatedItems: string[] | undefined;
  if (Array.isArray(rawRelated) && rawRelated.length > 0) {
    const primary = (itemNumber || '').trim().toUpperCase();
    const norm = [
      ...new Set(
        rawRelated
          .map(x =>
            String(x || '')
              .trim()
              .toUpperCase()
          )
          .filter(Boolean)
      ),
    ].filter(a => a !== primary);
    if (norm.length) relatedItems = norm;
  }

  const listing: BrandTraxListing = {
    url: reviewListing.url,
    domain,
    title: reviewListing.title,
    body: description,
    brand,
    product,
    productCategory: brandIpMerchandise ? articleCategory || undefined : storeProduct?.category,
    analysisProfile: brandIpMerchandise ? 'BRAND_IP_MERCHANDISE' : undefined,
    platform,
    country,
    seller: reviewListing.seller || (undefined as unknown as string),
    sellerStoreUrl,
    listingStatus: ListingStatus.ACTIVE,
    state: ListingState.NEW,
    itemNumber,
    relatedItems,
    price: price,
    stock: !isNaN(stock as number) ? stock : undefined,
    numberSold: !isNaN(numberSold as number) ? numberSold : undefined,
    listedCurrency: currency || undefined,
    created: new Date().toISOString(),
    images: uniqueImages.length > 0 ? uniqueImages : undefined,
    // Add screenshot if available (store service expects filename only)
    screenshots: screenshotFilename
      ? [
          {
            image: screenshotFilename,
            timestamp: new Date().toISOString(),
          },
        ]
      : undefined,
  };

  return listing;
}

/**
 * Extract item number (e.g. ASIN) from marketplace URLs when not provided by the API.
 * Supports Amazon /dp/ASIN, /gp/product/ASIN, /gp/aw/d/ASIN and similar patterns.
 * Exported for use when building payloads so itemNumber is always sent when derivable.
 */
export function extractItemNumberFromUrl(url: string): string | undefined {
  if (!url || typeof url !== 'string') return undefined;
  try {
    // Amazon: /dp/ASIN, /gp/product/ASIN, /gp/aw/d/ASIN, /product/ASIN
    const amazonMatch = url.match(/\/(?:dp|gp\/product|gp\/aw\/d|product)\/([A-Z0-9]{10})/i);
    if (amazonMatch) return amazonMatch[1];
    // Generic pattern: last path segment that looks like an ID (e.g. 10 alphanumeric)
    const pathSegment = url.split('?')[0].split('/').filter(Boolean).pop();
    if (pathSegment && /^[A-Z0-9]{8,15}$/i.test(pathSegment)) return pathSegment;
  } catch {
    /* ignore */
  }
  return undefined;
}

/**
 * Determines country based on marketplace or domain
 */
function determineCountry(marketplace?: string): Country {
  if (!marketplace) return Country.UNKNOWN;

  const marketplaceLower = marketplace.toLowerCase();

  // Common marketplace to country mappings
  if (marketplaceLower.includes('amazon.com')) return Country.US;
  if (marketplaceLower.includes('amazon.co.uk')) return Country.GB;
  if (marketplaceLower.includes('amazon.de')) return Country.DE;
  if (marketplaceLower.includes('amazon.fr')) return Country.FR;
  if (marketplaceLower.includes('amazon.it')) return Country.IT;
  if (marketplaceLower.includes('amazon.es')) return Country.ES;
  if (marketplaceLower.includes('amazon.ca')) return Country.CA;
  if (marketplaceLower.includes('amazon.com.au')) return Country.AU;
  if (marketplaceLower.includes('amazon.co.jp')) return Country.JP;
  if (marketplaceLower.includes('ebay.com')) return Country.US;
  if (marketplaceLower.includes('ebay.co.uk')) return Country.GB;
  if (marketplaceLower.includes('ebay.de')) return Country.DE;
  if (marketplaceLower.includes('tokopedia.com')) return Country.ID;
  if (marketplaceLower.includes('shopee.com')) return Country.SG;
  if (marketplaceLower.includes('lazada.com')) return Country.SG;
  if (marketplaceLower.includes('mercari.com')) return Country.JP;
  if (marketplaceLower.includes('rakuten.com')) return Country.JP;
  if (marketplaceLower.includes('alibaba.com')) return Country.CN;
  if (marketplaceLower.includes('taobao.com')) return Country.CN;
  if (marketplaceLower.includes('tmall.com')) return Country.CN;
  if (marketplaceLower.includes('dhgate.com')) return Country.CN;
  if (marketplaceLower.includes('made-in-china.com')) return Country.CN;
  if (marketplaceLower.includes('1688.com')) return Country.CN;

  // Try to extract country code from domain
  const domainParts = marketplace.split('.');
  if (domainParts.length >= 2) {
    const tld = domainParts[domainParts.length - 1];
    const countryCode = tld.toUpperCase();

    // Check if it's a valid country code
    if (Object.values(Country).includes(countryCode as Country)) {
      return countryCode as Country;
    }
  }

  return Country.UNKNOWN;
}

/**
 * Parses price string and extracts numeric value and currency
 */
/**
 * Parse price with marketplace-aware currency detection
 */
function parsePriceWithMarketplace(
  priceStr?: string,
  url?: string
): { price?: number; currency: string } {
  if (!priceStr) return { currency: 'USD' };

  // Try to detect currency from URL/marketplace first
  let currency = 'USD'; // default

  if (url) {
    if (url.includes('tokopedia')) {
      currency = 'IDR';
    } else if (url.includes('amazon')) {
      currency = 'USD';
    } else if (url.includes('ebay')) {
      currency = 'USD';
    } else {
      // Fall back to parsing the price string
      currency = parsePrice(priceStr).currency;
    }
  } else {
    currency = parsePrice(priceStr).currency;
  }

  // Clean price based on detected currency
  let cleanPrice = priceStr;

  // Remove currency symbols
  const currencySymbols = [
    'RM',
    'Rp',
    'IDR',
    'R$',
    '฿',
    '₫',
    '$',
    'COP',
    'zł',
    '₱',
    '€',
    'KRW',
    '원',
    'won',
    'one',
    'TL',
    'BRL',
    'جنيه',
    'EGP',
    '₹',
    '¥',
    'SAR',
    'ريال',
    'S$',
    'kr',
    'SEK',
    'AED',
    'Now',
    'U',
    'Kč',
    'DKK',
    '£',
    'Ft',
    '₪',
    'HUF',
    'NIS',
    '￥',
    'NOK',
    'QR.',
    'RON',
    'R',
    '₩',
    'CHF',
    '₺',
    'EUR',
    'GBP',
    'PHP',
    'USD',
    'Rs.',
    '€00',
    'zloty',
    'each',
    '₦',
    '￡',
    '₽',
    'B',
    'лв',
    'FF',
    'Kh',
    'H₵',
    'hs',
    'T',
    'X',
    'грн',
    'tenge.',
    'lei',
    'H',
    'yen',
    '円',
    '₴',
    'Yen',
    'circle',
  ];

  for (const symbol of currencySymbols) {
    cleanPrice = cleanPrice.replace(symbol, '');
  }

  // Apply currency-specific cleansing rules
  if (currency === 'IDR') {
    // IDR: periods are thousands separators, remove them
    cleanPrice = cleanPrice.replace(/\./g, '').replace(/,/g, '');
  } else if (currency === 'BRL') {
    // BRL: periods are thousands separators, commas are decimals
    const parts = cleanPrice.split(',');
    cleanPrice = parts[0].replace(/\./g, '');
    if (parts.length > 1) {
      cleanPrice += '.' + parts[1];
    }
  } else if (currency === 'EUR') {
    // EUR: periods are thousands separators, commas are decimals
    const parts = cleanPrice.split(',');
    cleanPrice = parts[0].replace(/\./g, '');
    if (parts.length > 1) {
      cleanPrice += '.' + parts[1];
    }
  } else if (currency === 'PLN') {
    // PLN: commas become decimal points if there's a decimal part
    if (cleanPrice.includes(',') && cleanPrice.includes('.')) {
      cleanPrice = cleanPrice.replace(',', '.');
    } else {
      cleanPrice = cleanPrice.replace(/,/g, '');
    }
  } else {
    // Default: commas are thousands separators, periods are decimals
    cleanPrice = cleanPrice.replace(/,/g, '');
  }

  // Remove any remaining non-numeric characters except decimal points
  cleanPrice = cleanPrice.replace(/[^\d.-]/g, '');

  // Handle multiple decimal points (keep only the last one)
  const decimalParts = cleanPrice.split('.');
  if (decimalParts.length > 2) {
    cleanPrice = decimalParts.slice(0, -1).join('') + '.' + decimalParts[decimalParts.length - 1];
  }

  const numericPrice = parseFloat(cleanPrice);

  return {
    price: isNaN(numericPrice) ? undefined : numericPrice,
    currency,
  };
}

export function parsePrice(priceStr?: string): { price?: number; currency: string } {
  if (!priceStr) return { currency: 'USD' };

  // Extract currency from original string first
  let currency = 'USD'; // default

  if (priceStr.includes('$') && !priceStr.includes('€')) currency = 'USD';
  else if (priceStr.includes('€')) currency = 'EUR';
  else if (priceStr.includes('£')) currency = 'GBP';
  else if (priceStr.includes('¥'))
    currency = 'JPY'; // Japanese Yen
  else if (priceStr.includes('Rp') || priceStr.includes('IDR')) currency = 'IDR';
  else if (priceStr.includes('RM')) currency = 'MYR';
  else if (priceStr.includes('S$')) currency = 'SGD';
  else if (priceStr.includes('THB')) currency = 'THB';
  else if (priceStr.includes('₱')) currency = 'PHP';
  else if (priceStr.includes('₫')) currency = 'VND';
  else if (priceStr.includes('₹')) currency = 'INR';
  else if (priceStr.includes('₩')) currency = 'KRW';
  else if (priceStr.includes('zł')) currency = 'PLN';
  else if (priceStr.includes('R$')) currency = 'BRL';
  else if (priceStr.includes('COP')) currency = 'COP';

  // Clean price based on currency rules
  let cleanPrice = priceStr;

  // Remove currency symbols
  const currencySymbols = [
    'RM',
    'Rp',
    'IDR',
    'R$',
    '฿',
    '₫',
    '$',
    'COP',
    'zł',
    '₱',
    '€',
    'KRW',
    '원',
    'won',
    'one',
    'TL',
    'BRL',
    'جنيه',
    'EGP',
    '₹',
    '¥',
    'SAR',
    'ريال',
    'S$',
    'kr',
    'SEK',
    'AED',
    'Now',
    'U',
    'Kč',
    'DKK',
    '£',
    'Ft',
    '₪',
    'HUF',
    'NIS',
    '￥',
    'NOK',
    'QR.',
    'RON',
    'R',
    '₩',
    'CHF',
    '₺',
    'EUR',
    'GBP',
    'PHP',
    'USD',
    'Rs.',
    '€00',
    'zloty',
    'each',
    '₦',
    '￡',
    '₽',
    'B',
    'лв',
    'FF',
    'Kh',
    'H₵',
    'hs',
    'T',
    'X',
    'грн',
    'tenge.',
    'lei',
    'H',
    'yen',
    '円',
    '₴',
    'Yen',
    'circle',
  ];

  for (const symbol of currencySymbols) {
    cleanPrice = cleanPrice.replace(symbol, '');
  }

  // Apply currency-specific cleansing rules
  if (currency === 'IDR') {
    // IDR: periods are thousands separators, remove them
    cleanPrice = cleanPrice.replace(/\./g, '').replace(/,/g, '');
  } else if (currency === 'BRL') {
    // BRL: periods are thousands separators, commas are decimals
    const parts = cleanPrice.split(',');
    cleanPrice = parts[0].replace(/\./g, '');
    if (parts.length > 1) {
      cleanPrice += '.' + parts[1];
    }
  } else if (currency === 'EUR') {
    // EUR: periods are thousands separators, commas are decimals
    const parts = cleanPrice.split(',');
    cleanPrice = parts[0].replace(/\./g, '');
    if (parts.length > 1) {
      cleanPrice += '.' + parts[1];
    }
  } else if (currency === 'PLN') {
    // PLN: commas become decimal points if there's a decimal part
    if (cleanPrice.includes(',') && cleanPrice.includes('.')) {
      cleanPrice = cleanPrice.replace(',', '.');
    } else {
      cleanPrice = cleanPrice.replace(/,/g, '');
    }
  } else {
    // Default: commas are thousands separators, periods are decimals
    cleanPrice = cleanPrice.replace(/,/g, '');
  }

  // Remove any remaining non-numeric characters except decimal points
  cleanPrice = cleanPrice.replace(/[^\d.-]/g, '');

  // Handle multiple decimal points (keep only the last one)
  const decimalParts = cleanPrice.split('.');
  if (decimalParts.length > 2) {
    cleanPrice = decimalParts.slice(0, -1).join('') + '.' + decimalParts[decimalParts.length - 1];
  }

  const numericPrice = parseFloat(cleanPrice);

  return {
    price: isNaN(numericPrice) ? undefined : numericPrice,
    currency,
  };
}
